import * as React from 'react'

import { inject, observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import AvatarType from '~/client/src/shared/enums/AvatarType'
import User from '~/client/src/shared/models/User'
import ProjectRolesStore from '~/client/src/shared/stores/domain/ProjectRoles.store'
import { INTERPUNCT, NO_VALUE } from '~/client/src/shared/utils/usefulStrings'

import UserProject from '../../models/UserProject'
import CompaniesStore from '../../stores/domain/Companies.store'
import UserProjectsStore from '../../stores/domain/UserProjects.store'
import Avatar, { AvatarSize } from '../Avatar/Avatar'
import AvatarInput from '../AvatarInput/AvatarInput'
import UsernameFromUid from '../UsernameFromUid'

import './UserProfilePreview.scss'

interface IProps {
  user: User

  className?: string
  contentContainerClassName?: string

  briefInfoClassName?: string
  isBriefInfoHidden?: boolean
  unknownUserLabel?: string

  avatarSize?: AvatarSize
  isAvatarUpdateAllowed?: boolean
  onAvatarChange?: (newUrl: string) => void
  newAvatarUrl?: string

  shouldSplitToRows?: boolean
  shouldUseExtraLargeFont?: boolean

  isOnline?: boolean

  isUserNameClickable?: boolean

  projectRolesStore?: ProjectRolesStore
  companiesStore?: CompaniesStore
  userProjectsStore?: UserProjectsStore
  isCompact?: boolean
  withEmployeeId?: boolean
  withJobTitle?: boolean
  withCompany?: boolean
  withRoles?: boolean
  withContactInfo?: boolean
  showBoldName?: boolean
  isAlwaysUnderlined?: boolean
}

@inject('projectRolesStore', 'companiesStore', 'userProjectsStore')
@observer
export default class UserProfilePreview extends React.Component<IProps> {
  public static defaultProps = {
    isUserNameClickable: true,
    showBoldName: true,
  }

  private get user(): User {
    return this.props.user || User.service
  }

  public render() {
    const {
      avatarSize,
      className,
      isBriefInfoHidden: isInfoHidden,
      isAvatarUpdateAllowed,
      newAvatarUrl,
      onAvatarChange,
      isOnline,
      shouldUseExtraLargeFont,
      isUserNameClickable,
      shouldSplitToRows,
      withEmployeeId,
      withContactInfo,
      isCompact,
      unknownUserLabel,
      showBoldName,
      contentContainerClassName,
      isAlwaysUnderlined,
    } = this.props

    const { avatarUrl, id: userId } = this.user

    const initials = User.getInitialsToDisplay(this.user)
    const displayedAvatarUrl = isAvatarUpdateAllowed ? newAvatarUrl : avatarUrl
    const avatar = isAvatarUpdateAllowed ? (
      <AvatarInput
        data={{ initials, url: displayedAvatarUrl }}
        onChange={onAvatarChange}
        avatarType={AvatarType.User}
        size={avatarSize}
        withEditor={true}
      />
    ) : (
      <Avatar
        src={displayedAvatarUrl}
        size={avatarSize}
        initials={initials}
        isOnline={shouldSplitToRows ? null : isOnline}
      />
    )

    if (shouldSplitToRows) {
      return this.renderProfileAsSeparateRows(avatar, this.user, isOnline)
    }

    return (
      <div
        className={classList({
          'user-profile-preview row overflow-hidden': true,
          [className]: !!className,
        })}
      >
        <div className="no-grow">{avatar}</div>
        {!isCompact && (
          <div className="user-profile-preview-additional-info overflow-hidden">
            <div className="col pl8 overflow-hidden">
              <div
                className={classList({
                  'text name-row ellipsis': true,
                  large: !shouldUseExtraLargeFont,
                  'extra-large': shouldUseExtraLargeFont,
                  [contentContainerClassName]: true,
                })}
              >
                <UsernameFromUid
                  userId={userId}
                  isClickable={isUserNameClickable}
                  unknownUserLabel={unknownUserLabel}
                  showBoldName={showBoldName}
                  isAlwaysUnderlined={isAlwaysUnderlined}
                />
              </div>
              {!isInfoHidden && this.renderBriefInfo()}
              {withEmployeeId && this.renderEmployeeId()}
              {withContactInfo && this.renderContactInfo()}
            </div>
          </div>
        )}
      </div>
    )
  }

  private renderBriefInfo(): JSX.Element {
    if (this.user.isService) return

    const { briefInfoClassName } = this.props

    return (
      <div
        className={classList({
          'text large light ellipsis brief-info-row': true,
          [briefInfoClassName]: !!briefInfoClassName,
        })}
      >
        {this.company} {INTERPUNCT} {this.briefDetails}
      </div>
    )
  }

  private renderContactInfo(): JSX.Element {
    if (this.user.isService || !this.user.email) return null

    const { briefInfoClassName } = this.props

    return (
      <div
        className={classList({
          'text large light ellipsis brief-info-row': true,
          [briefInfoClassName]: !!briefInfoClassName,
        })}
      >
        {this.user.email}
      </div>
    )
  }

  private renderEmployeeId(): JSX.Element {
    return (
      this.user.employeeId && (
        <div className="text monospace light lp05">{this.user.employeeId}</div>
      )
    )
  }

  private get userProjectSettings(): UserProject {
    return this.props.userProjectsStore.getByUser(this.user)
  }

  private get company(): string {
    return this.props.companiesStore.getCompanyNameById(
      this.userProjectSettings.companyId,
    )
  }

  private get jobTitle(): string {
    return this.user.globalRole || ''
  }

  private get projectRoles(): string {
    return this.props.projectRolesStore.getInstancesAsStringByIds(
      this.userProjectSettings.roles,
    )
  }

  private get roles(): string {
    return this.projectRoles || NO_VALUE
  }

  private get briefDetails(): string {
    return (
      [this.jobTitle, this.projectRoles]
        .filter(Boolean)
        .join(` ${INTERPUNCT} `) || NO_VALUE
    )
  }

  private renderProfileAsSeparateRows(
    Avatar: JSX.Element,
    user: User,
    isOnline: boolean,
  ): JSX.Element {
    const { withContactInfo, withJobTitle, withCompany, withRoles, className } =
      this.props
    return (
      <>
        {Avatar}
        <div
          className={classList({
            'user-profile-preview container': true,
            [className]: !!className,
          })}
        >
          <span className="user-name">
            <UsernameFromUid
              userId={user.id}
              isClickable={false}
              unknownUserLabel={this.props.unknownUserLabel}
            />
            <span
              className={classList({
                dot: isOnline !== null,
                online: isOnline,
              })}
            ></span>
          </span>

          {withJobTitle && this.jobTitle && (
            <div className="brief-info">{this.jobTitle}</div>
          )}
          {withCompany && <div className="text brief-info">{this.company}</div>}
          {withRoles && <div className="text brief-info">{this.roles}</div>}
          {withContactInfo && (
            <div className="text brief-info">{this.user.email}</div>
          )}
        </div>
      </>
    )
  }
}
